import { useCallback, useEffect, useRef, useState } from "react";

/** El monitor se deja abierto horas; sin check explícito un deploy tarda en notarse. */
const UPDATE_CHECK_INTERVAL_MS = 30 * 60 * 1000;

/**
 * useServiceWorkerUpdate — Detecta que hay una versión nueva esperando.
 *
 * El service worker NO llama `skipWaiting()` en install a propósito: se queda en
 * estado `waiting` hasta que el usuario acepta. Recargar sin avisar a alguien
 * que está mirando el mapa en vivo es hostil, y peor si está en medio de un
 * reporte. Este hook expone el aviso y `applyUpdate()` lo consuma.
 *
 * @returns {{ updateAvailable: boolean, applyUpdate: () => void }}
 */
export function useServiceWorkerUpdate() {
  const [waitingWorker, setWaitingWorker] = useState(null);
  const registrationRef = useRef(null);

  useEffect(() => {
    // Chequeo por valor y no `"serviceWorker" in navigator`: en contextos no
    // seguros (http por IP de LAN) la propiedad existe pero vale undefined.
    if (typeof navigator === "undefined" || !navigator.serviceWorker) {
      return undefined;
    }

    let cancelled = false;
    // Listeners a soltar en el cleanup. Sin esto se acumulan sobre el mismo
    // objeto `registration`, que sobrevive al desmontaje del componente (en
    // StrictMode el efecto ya corre dos veces de entrada).
    const detachers = [];

    function on(target, event, handler) {
      target.addEventListener(event, handler);
      detachers.push(() => target.removeEventListener(event, handler));
    }

    function promote(worker) {
      // `controller` no nulo ⇒ ya había un SW controlando la página ⇒ esto es
      // una actualización. En la primera instalación no hay nada que avisar.
      if (worker && navigator.serviceWorker.controller && !cancelled) {
        setWaitingWorker(worker);
      }
    }

    function watch(registration) {
      registrationRef.current = registration;
      promote(registration.waiting);

      on(registration, "updatefound", () => {
        const installing = registration.installing;
        if (!installing) return;
        on(installing, "statechange", () => {
          if (installing.state === "installed") promote(installing);
        });
      });
    }

    navigator.serviceWorker.ready
      .then((registration) => {
        if (!cancelled) watch(registration);
      })
      .catch(() => {
        // Sin registración activa no hay nada que vigilar.
      });

    function checkForUpdate() {
      if (document.visibilityState !== "visible") return;
      registrationRef.current?.update?.()?.catch?.(() => {});
    }

    document.addEventListener("visibilitychange", checkForUpdate);
    const intervalId = setInterval(checkForUpdate, UPDATE_CHECK_INTERVAL_MS);

    return () => {
      cancelled = true;
      clearInterval(intervalId);
      document.removeEventListener("visibilitychange", checkForUpdate);
      detachers.forEach((detach) => detach());
    };
  }, []);

  const applyUpdate = useCallback(() => {
    if (!waitingWorker || !navigator.serviceWorker) return;
    // `controllerchange` llega cuando el SW nuevo toma control tras skipWaiting.
    // El listener se registra acá y no en el efecto para que la recarga sea
    // siempre consecuencia de un clic del usuario, nunca de un claim automático.
    navigator.serviceWorker.addEventListener(
      "controllerchange",
      () => {
        window.location.reload();
      },
      { once: true },
    );
    waitingWorker.postMessage({ type: "SKIP_WAITING" });
  }, [waitingWorker]);

  return { updateAvailable: waitingWorker !== null, applyUpdate };
}
